import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { toast } from "sonner";
import { ExternalLink, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { photoUrl, priceLabel, money } from "../../lib/market";
import { AgreementCard, StoreCard } from "../../components/market/SellerSetup";
import { ListingForm } from "../../components/market/ListingForm";

function MyListings({ rows, reload }) {
  async function setStatus(l, status) {
    if (status === "removed" && !window.confirm(`Remove "${l.title}"?`)) return;
    try { await api.patch(`/market/listings/${l.listing_id}`, { status }); reload(); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="space-y-3" data-testid="my-listings">
      <p className="font-serif text-3xl">My listings ({rows.length})</p>
      {!rows.length && <p className="text-[var(--muted)]">Nothing posted yet.</p>}
      {rows.map((l) => (
        <div key={l.listing_id} className="flex items-center gap-4 bg-white border border-[var(--line)] rounded-2xl p-3" data-testid={`my-listing-${l.listing_id}`}>
          <img src={photoUrl(l.photos?.[0])} alt="" className="w-16 h-16 rounded-xl object-cover" />
          <div className="flex-1 min-w-0"><b className="block truncate">{l.title}</b><span className="text-xs text-[var(--muted)]">{priceLabel(l)} · {l.status} · {l.views || 0} views</span></div>
          <Link to={`/market/item/${l.listing_id}`} className="p-2 text-[var(--muted)] hover:text-[var(--gold)]" title="View"><ExternalLink className="w-4 h-4" /></Link>
          {l.sold_order_id ? <span className="text-xs text-emerald-700" data-testid={`my-listing-sold-site-${l.listing_id}`}>Sold via site</span> : l.status === "active" ? <button onClick={() => setStatus(l, "sold")} className="text-xs underline" data-testid={`my-listing-sold-${l.listing_id}`}>Mark {l.kind === "sale" ? "sold" : "gone"}</button> : <button onClick={() => setStatus(l, "active")} className="text-xs underline" data-testid={`my-listing-relist-${l.listing_id}`}>Relist</button>}
          <button onClick={() => setStatus(l, "removed")} className="text-xs text-red-600 underline" data-testid={`my-listing-remove-${l.listing_id}`}>Remove</button>
        </div>
      ))}
    </div>
  );
}

function Sales({ s }) {
  if (!s?.orders.length) return null;
  return (
    <div className="space-y-3" data-testid="my-sales">
      <p className="font-serif text-3xl">Sales</p>
      <div className="grid grid-cols-3 gap-3 text-center">
        {[["Total sales", s.total_sales, "sales-total"], ["Owed to you", s.owed, "sales-owed"], ["Paid out", s.paid_out, "sales-paid"]].map(([l, v, id]) => <div key={l} className="bg-white border border-[var(--line)] rounded-2xl p-4"><p className="text-2xl" data-testid={id}>{money(v)}</p><p className="text-xs text-[var(--muted)]">{l}</p></div>)}
      </div>
      {s.orders.map((o) => <div key={o.order_id} className="flex justify-between text-sm bg-white border border-[var(--line)] rounded-2xl px-4 py-3"><span>{o.title} · {o.buyer_email}</span><span>{money(o.amount)} − {o.fee_percent}% = <b>{money(o.seller_net)}</b> · {o.payout_status === "paid" ? "Paid out" : "Payout pending"}</span></div>)}
    </div>
  );
}

export default function MarketplaceDashboard() {
  const [me, setMe] = useState(null);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [sales, setSales] = useState(null);
  const load = useCallback(() => {
    api.get("/market/me").then((r) => setMe(r.data));
    api.get("/market/mine").then((r) => setRows(r.data));
    api.get("/market/sales").then((r) => setSales(r.data));
  }, []);
  useEffect(() => { load(); api.get("/market/meta").then((r) => setMeta(r.data)); }, [load]);
  if (!me) return null;
  return (
    <div className="space-y-8 max-w-4xl" data-testid="marketplace-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="font-script text-[var(--gold)] text-3xl">Wedding Consignment</p><h1 className="font-serif font-light text-4xl">Sell, rent or donate</h1></div>
        <div className="flex gap-2"><Link to="/market/inbox" className="btn-outline-gold !py-2.5" data-testid="dash-market-inbox"><MessageCircle className="w-4 h-4" /> Messages</Link><Link to="/market" className="btn-gold !py-2.5" data-testid="dash-market-browse">Browse market</Link></div>
      </div>
      <StoreCard store={me.store} onDone={load} />
      {!me.agreed ? <AgreementCard meta={meta} onDone={load} pending={me.store?.status === "pending"} /> : me.can_sell ? <ListingForm meta={meta} onDone={load} /> : null}
      <Sales s={sales} />
      <MyListings rows={rows} reload={load} />
    </div>
  );
}

export function MarketSellPage() {
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Navbar />
      <div className="container-wide px-5 md:px-12 pt-28 pb-20"><MarketplaceDashboard /></div>
    </div>
  );
}
